import React from "react";
import { Form, Row, Col, Checkbox, Button, Input, Descriptions, Upload, Badge, notification } from "antd";
import { UploadOutlined, LoadingOutlined, ClockCircleOutlined } from "@ant-design/icons";
import "./ResolutionForm.css";

const ResolutionForm = ({
  form,
  onSubmit,
  onFinishFailed,
  runHardwareTest,
  testResult,
  isokSpinner,
  resolutionSpinner,
  isImpersonated,
  todoSelected,
  type,
  attachments,
  setAttachments,
  fileError,
  setFileError,
  isFTNotesFetching,
  FTNotes,
  setChildDrawerNotesVisible,
}) => {
  const beforeUpload = (file) => {
    const allowedTypes = ["text/csv", "image/png", "image/jpeg", "application/pdf"];
    const isAllowedType = allowedTypes.includes(file.type);

    if (!isAllowedType) {
      notification.error({ message: "Only CSV, PDF or image files are allowed." });
      return Upload.LIST_IGNORE;
    }

    if (file.size / 1024 / 1024 > 5) {
      notification.error({ message: "File must be smaller than 5MB." });
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const beautifyTestResult = (testResult) => {
    const items = testResult.map((result) => (
      <Descriptions.Item style={{ whiteSpace: "pre-wrap" }} span={24} key={result}>
        {result}
      </Descriptions.Item>
    ));
    return <>{items}</>;
  };

  return (
    <Form
      form={form}
      onFinish={(values) => {
        values.attachments = attachments;
        onSubmit(values);
      }}
      onFinishFailed={onFinishFailed}
    >
      {todoSelected.delegated_status === "Hold - Batched H&E" && (
        <Form.Item name="checkbox-group">
          <Checkbox.Group>
            <Row>
              <Col span={8}>
                <Checkbox value="Rebooted" style={{ lineHeight: "32px" }}>
                  Rebooted
                </Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value="Rescued" style={{ lineHeight: "32px" }}>
                  Rescued
                </Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value="Replaced disk" style={{ lineHeight: "32px" }}>
                  Replaced disk
                </Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value="Reseated components" style={{ lineHeight: "32px" }}>
                  Reseated components
                </Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value="Replaced RAM" style={{ lineHeight: "32px" }}>
                  Replaced RAM
                </Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value="Replaced Machine" style={{ lineHeight: "32px" }}>
                  Replaced Machine
                </Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value="Identified Faulty disk" style={{ lineHeight: "32px" }}>
                  Identified Faulty disk
                </Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value="Replaced ETH/Power cable" style={{ lineHeight: "32px" }}>
                  Replaced ETH/Power cable
                </Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value="Clean/Replaced optic" style={{ lineHeight: "32px" }}>
                  Clean/Replaced optic
                </Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value="Other" style={{ lineHeight: "32px" }}>
                  Other
                </Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Form.Item>
      )}
      {todoSelected.ip_asset_ip_address && todoSelected.delegated_status === "Hold - Batched H&E" && (
        <Descriptions.Item span={3} offset={4}>
          <Button
            type="primary"
            htmlType="submit"
            style={{ marginRight: "30px", top: "-4px", display: "inline-flex", marginBottom: "10px" }}
            onClick={runHardwareTest}
            disabled={isokSpinner || isImpersonated}
          >
            Run Hardware Test
            {isokSpinner && <LoadingOutlined style={{ fontSize: 20 }} spin />}
          </Button>
        </Descriptions.Item>
      )}
      {testResult.value && !isokSpinner && todoSelected.delegated_status === "Hold - Batched H&E" && (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="HardwareTestResults"
              id="Test Results"
              label="Hardware Test Results"
              style={{
                flexDirection: "column",
                flexWrap: "wrap",
                textAlign: "left",
                marginBottom: "12px",
              }}
              initialValue={testResult.value}
            >
              <Descriptions colon={true} size="small" labelStyle={{ paddingRight: "0px" }} style={{ backgroundColor: "#E8E8E8", padding: "5px 15px" }}>
                {beautifyTestResult(testResult.value)}
                {testResult.updated_date && (
                  <Descriptions.Item
                    span={24}
                    label="Last Updated Date"
                    labelStyle={{ fontWeight: 500, fontSize: "12px", fontStyle: "italic" }}
                    contentStyle={{ fontWeight: 500, fontSize: "12px", fontStyle: "italic" }}
                  >
                    {testResult.updated_date}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="Return Tracking"
            label="Return Tracking"
            id="Return Tracking"
            style={{
              flexDirection: "column",
              flexWrap: "wrap",
              textAlign: "left",
            }}
            rules={type === "todos" ? [{ whitespace: true }] : [{ whitespace: true }, { required: true, message: "Return tracking is required!" }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <div style={{ float: "right", position: "absolute", right: 0, zIndex: 10 }}>
            <Button type="link" onClick={() => setChildDrawerNotesVisible(true)} style={{ padding: "0px" }}>
              <Badge count={isFTNotesFetching ? <ClockCircleOutlined style={{ color: "#f5222d" }} /> : FTNotes?.data.length}>
                <div style={{ color: "#1890ff" }}>Add Notes&nbsp;&nbsp;&nbsp;</div>
              </Badge>
            </Button>
          </div>
          <Form.Item
            name="Comments"
            label="Comment"
            style={{
              flexDirection: "column",
              flexWrap: "wrap",
              textAlign: "left",
              paddingBottom: 12,
            }}
            rules={[
              { required: true, message: "Comment is required!" },
              { whitespace: true, message: "Comment is required!" },
            ]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Col>
      </Row>
      {type === "todos" && (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="attachments"
              label="Attachments:"
              extra="Upload CSV, PDF, or image files (Max 5MB each, up to 10 files)"
            >
              <React.Fragment>
                <Upload
                  accept=".csv,.png,.jpeg,.jpg,.pdf"
                  beforeUpload={beforeUpload}
                  multiple
                  fileList={attachments}
                  onChange={({ fileList }) => {
                    let error = "";
                    const unique = [];
                    const seen = new Set();

                    for (let file of fileList) {
                      const key = `${file.name}-${file.size}`;
                      if (seen.has(key)) {
                        error = "Duplicate files are not allowed!";
                        continue;
                      }
                      seen.add(key);
                      unique.push(file);
                    }

                    if (unique.length > 10) {
                      error = "Maximum of 10 files can be uploaded!";
                      unique.splice(10);
                    }

                    setAttachments(unique);
                    setFileError(error);
                  }}
                >
                  <Button type="primary" icon={<UploadOutlined />}>
                    Attach File
                  </Button>
                </Upload>

                {fileError && <div style={{ color: "red", marginTop: 8 }}>{fileError}</div>}
              </React.Fragment>
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={16}>
        <Col style={{ maxWidth: "none" }}>
          <Form.Item>
            {!resolutionSpinner && (
              <Button
                type="primary"
                htmlType="submit"
                disabled={(!testResult.value && isokSpinner) || isImpersonated}
                style={{ display: "inline-flex" }}
              >
                Submit
              </Button>
            )}
            {resolutionSpinner && (
              <Button
                type="primary"
                htmlType="submit"
                style={{ display: "inline-flex" }}
                disabled={true}
              >
                Submit
                {resolutionSpinner && <LoadingOutlined style={{ fontSize: 20, color: "grey", marginBottom: 2 }} spin />}
              </Button>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default ResolutionForm;